'use client';
import React, { useEffect, useState } from 'react';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { TRANSLATIONS } from './i18n';
import { SortFilterIcon } from './SortFilterIcon';
import { useSortFilterOverlay } from './SortFilterOverlayContext';

/**
 * The button that opens the Sort & Filter overlay from the personal loan bar, on
 * the row of the Loan Amount and Tenure fields, from `md` to `lg`, where the
 * panel is a drawer (CMS-2072).
 *
 * It renders only while the page has a floating panel (a pinned one keeps its
 * own pill), and the panel's own pill gives way to it only while it is on screen:
 * the bar is hidden past the last block of its range, and the pill has to be back
 * for the rest of the results. Its label is the market's "All Filters", not the
 * editor's panel title. Both happen after hydration: the bar is portalled
 * in from a block, so it is not in the server HTML either, and the pill is what
 * the first paint shows.
 */
const CLASSES =
  'mhg-alpha-typography-button-2 ml-auto hidden h-9 shrink-0 items-center gap-1 self-end whitespace-nowrap rounded-lg border border-borders/default bg-buttons/base px-2.5 text-buttons/primary hover:bg-fill/alternative-1 md:inline-flex lg:hidden';

/** Whether the node takes up space: not `display: none` itself or under a hidden bar. */
const useIsDisplayed = (node: HTMLElement | null): boolean => {
  const [displayed, setDisplayed] = useState(false);

  useEffect(() => {
    if (!node) return;
    const measure = () => setDisplayed(node.getClientRects().length > 0);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    // Fires when the size goes to or from zero, which `display: none` does.
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => {
      observer.disconnect();
      setDisplayed(false);
    };
  }, [node]);

  return displayed;
};

export const SortFilterBarButton: React.FC = () => {
  const { brand, lang } = useCmsClientContext();
  const overlay = useSortFilterOverlay();
  const [button, setButton] = useState<HTMLButtonElement | null>(null);
  const displayed = useIsDisplayed(button);
  const registerBarButton = overlay?.registerBarButton;
  const panelTitle = overlay?.panelTitle ?? null;

  useEffect(() => {
    if (!registerBarButton || !displayed) return;
    return registerBarButton();
  }, [registerBarButton, displayed]);

  if (!overlay || panelTitle === null) return null;

  const t = getClientTranslations(TRANSLATIONS, { brand, lang });

  return (
    <button
      ref={setButton}
      type="button"
      data-component="sort-filter-bar-button"
      onClick={() => overlay.open(button)}
      aria-haspopup="dialog"
      aria-expanded={overlay.isOpen}
      // The overlay mounts only while open, so a closed id would dangle.
      aria-controls={overlay.isOpen ? overlay.panelId : undefined}
      className={CLASSES}
    >
      {/* The design's 22px; no spacing step is that size. */}
      {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
      <SortFilterIcon className="size-[22px] shrink-0" />
      {t.sort_and_filter_all_filters}
    </button>
  );
};
